/**
 * UUID v7 — identificador ordenavel por tempo (RFC 9562).
 *
 * Motivo de existir em vez de `crypto.randomUUID()` (v4): os IDs sao gerados no
 * CLIENTE para permitir criacao offline (spec 3). Um ID ordenavel por tempo
 * mantem a insercao no indice primario do SQLite sequencial e da uma ordem
 * estavel de desempate sem consultar o servidor.
 *
 * Layout (128 bits):
 *   48 bits  timestamp unix em ms, big-endian
 *    4 bits  versao = 7
 *   12 bits  aleatorio (rand_a) — desempate dentro do mesmo milissegundo
 *    2 bits  variante = 0b10
 *   62 bits  aleatorio (rand_b)
 */

const HEX = '0123456789abcdef';

function toHex(bytes: Uint8Array): string {
  let out = '';
  for (const b of bytes) {
    out += HEX[(b >>> 4) & 0x0f]! + HEX[b & 0x0f]!;
  }
  return out;
}

/**
 * Gera um UUID v7 usando `crypto.getRandomValues` (disponivel em Workers,
 * browsers e Node >= 19).
 *
 * @param now epoch em ms; injetavel para tornar o teste deterministico.
 */
export function uuidv7(now: number = Date.now()): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);

  const ts = Math.floor(now);
  // 48 bits de timestamp. `ts` cabe com folga em Number.MAX_SAFE_INTEGER
  // (o limite de 2^48 ms so e atingido no ano 10889).
  bytes[0] = (ts / 2 ** 40) & 0xff;
  bytes[1] = (ts / 2 ** 32) & 0xff;
  bytes[2] = (ts / 2 ** 24) & 0xff;
  bytes[3] = (ts / 2 ** 16) & 0xff;
  bytes[4] = (ts / 2 ** 8) & 0xff;
  bytes[5] = ts & 0xff;

  // versao 7 nos 4 bits altos do byte 6
  bytes[6] = (bytes[6]! & 0x0f) | 0x70;
  // variante RFC 4122 (0b10) nos 2 bits altos do byte 8
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;

  const h = toHex(bytes);
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isUuid(value: string): boolean {
  return UUID_RE.test(value);
}

/** Extrai o timestamp (epoch ms) embutido em um UUID v7. */
export function uuidv7Timestamp(uuid: string): number {
  const hex = uuid.replace(/-/g, '').slice(0, 12);
  return Number.parseInt(hex, 16);
}
